import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import Skeleton from "react-loading-skeleton";
import type { RootState, AppDispatch } from "../../../../app/store";
import PaymentMethods from "./PaymentMethods";
import {
  setSelectedPaymentMethod,
  setIsCurrentStepFormValid,
} from "../../checkoutSlice";

interface PaymentStepContainerProps {
  availablePaymentMethods: string[];
  isLoading?: boolean;
  onPluxeeDataChange: (data: { gsm: string; otp: string }) => void;
}

const PaymentStepContainer: React.FC<PaymentStepContainerProps> = ({
  availablePaymentMethods,
  isLoading = false,
  onPluxeeDataChange,
}) => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { selectedPaymentMethod, currentStep } = useSelector(
    (state: RootState) => state.checkout
  );

  // This handler is for the radio button selection
  const handleMethodSelect = useCallback(
    (methodId: string) => {
      dispatch(setSelectedPaymentMethod(methodId));
    },
    [dispatch]
  );

  // This handler receives validity changes from the Pluxee form
  const handlePluxeeFormValidityChange = useCallback(
    (isValid: boolean) => {
      // Only dispatch if on the payment step and the method is pluxee
      if (currentStep === "payment" && selectedPaymentMethod === "pluxee_otp") {
        dispatch(setIsCurrentStepFormValid(isValid));
      }
    },
    [dispatch, currentStep, selectedPaymentMethod]
  );

  if (isLoading) {
    return (
      <div className="payment-step-container skeleton-payment">
        <h2>
          <Skeleton width={180} height={24} />
        </h2>
        <div className="payment-methods-skeleton">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="payment-option-skeleton">
              <Skeleton height={60} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="payment-step-container">
      <h2>{t("checkout.paymentMethod")}</h2>
      <PaymentMethods
        availableMethods={availablePaymentMethods}
        selectedMethodId={selectedPaymentMethod}
        onMethodSelect={handleMethodSelect}
        onPluxeeFormChange={onPluxeeDataChange}
        onPluxeeFormValidityChange={handlePluxeeFormValidityChange}
      />
    </div>
  );
};

export default PaymentStepContainer;
